<template lang="pug">
el-alert(
  v-if="ticketSopsLoadingState.error",
  :title="$t('genericError')",
  :closable="false",
  type="error",
  effect="dark"
)
template(v-else)
  .sop-view.mb-8(v-for="ticketSop in sopOverview", :key="ticketSop.sop.uuid")
    .flex.flex-row.items-center.mb-4.justify-between
      .flex.items-center
        h1.mr-4 {{ ticketSop.title }}
        el-popconfirm(
          v-if="isAdmin",
          :title="$t('confirmDelete')",
          :confirm-button-text="$t('delete')",
          :cancel-button-text="$t('cancel')",
          width="220",
          @confirm="removeSop(ticketSop.sop.sopRelationUuid)"
        )
          template(#reference)
            el-icon(v-if="isBeforeCompleted")
              Delete
      .flex.items-center
        .stats.text-slate-500 {{ $t("nOfMTasksCompleted", { n: tasksCompleted.get(ticketSop.sop.sopRelationUuid).completed, m: tasksCompleted.get(ticketSop.sop.sopRelationUuid).tasks }) }}
        el-icon.cursor-pointer.ml-4.mr-2(
          @click="switchVisibility(ticketSop.sop.sopRelationUuid)"
        ) 
          ArrowUpBold(
            v-if="hiddenSops.includes(ticketSop.sop.sopRelationUuid)"
          )
          ArrowDownBold(v-else)
    .sop-content(v-if="!hiddenSops.includes(ticketSop.sop.sopRelationUuid)")
      .sop-files.mb-8(v-if="ticketSop.sop.documents.length > 0")
        .flex.flex-row
          .font-semibold.mr-4 {{ $t("documents") }}
          .grid.grid-flow-col.auto-cols-max.gap-8
            .document.mb-2(
              v-for="doc in ticketSop.sop.documents",
              :key="doc.uuid"
            )
              a.text-accent.font-semibold(:href="`/api/documents/${doc.uuid}`") {{ `${doc.description} (${doc.name})` }}
      .sop-checklist
        template(
          v-for="item in ticketSop.sop.todoLists.flatMap((tdl) => tdl.items)",
          :key="item.uuid"
        )
          el-checkbox(
            v-if="item.completionTracking",
            size="large",
            :disabled="!isTicketInProcess",
            :model-value="item.instance?.completed",
            @update:model-value="(value: boolean) => setTodoItemChecked(value, ticketSop.sop.sopRelationUuid, item)"
          )
          .placehodler(v-else)
          span {{ item.description }}
          EditableText(
            v-if="item.feedback",
            :content="item.instance?.feedback",
            :disabled="!isTicketInProcess",
            :save-callback="(feedback) => saveFeedback(feedback, ticketSop.sop.sopRelationUuid, item)"
          )
            span(v-if="item.instance?.feedback") {{ item.instance?.feedback }}
            span.text-slate-400(v-else) {{ $t("feedback") }}
          .placeholder(v-else)
el-button(
  v-if="isAdmin && !isTicketFinished",
  icon="Plus",
  link,
  @click="showAddSop"
) {{ $t("addSop") }}

el-dialog(v-model="addSopVisible", :title="$t('addSop')")
  smx-form
    smx-form-item(:label="$t('sop')")
      SopSelect(v-model="selectedSop", :query="{ active: true }")
    smx-form-item(:label="$t('title')")
      el-input(v-model="title")
  template(#footer)
    el-button(type="danger", @click="closeAddSopDialog") {{ $t("cancel") }}
    el-button(
      type="primary",
      :disabled="!addSopAvailable",
      @click="addSelectedSop"
    ) {{ $t("add") }}
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import {
  addSopToTicket,
  getTicketSops,
  patchTodoItemInstance,
  type TicketSop,
  type SOP,
  type TodoListItem,
  removeSopFromTicket,
} from "../../../maintenance/service";
import { type TwinResponse } from "@shared/services/twinManagement";
import { type LoadState, stateInitial } from "@shared/models/state";
import { loadResource } from "@shared/util/loadResource";
import EditableText from "@shared/components/elements/EditableText.vue";
import SopSelect from "@shared/maintenance/components/SopSelect.vue";
import { useI18n } from "vue-i18n";
import { showResponseErrorToUser } from "@shared/util/genericResponseErrors";
import { type TicketManagementAuth } from "@shared/maintenance/hooks/maintenanceAuth";
import { statuses, ticketProps } from "../constants";

const props = defineProps<{
  ticket: TwinResponse;
  useAuth: () => TicketManagementAuth;
}>();

const auth = props.useAuth();

const ticketSops = computed(() => ticketSopsLoadingState.value.result);

const ticketSopsLoadingState = ref<LoadState<TicketSop[]>>({
  ...stateInitial,
  result: [],
});

async function initTicketSops() {
  await loadResource(
    () => getTicketSops(props.ticket.uuid),
    ticketSopsLoadingState,
  );
}

const tasksCompleted = computed(() => {
  const map = new Map();
  sopOverview.value.forEach((item) => {
    const itemsWithCompletionTracking = item.sop.todoLists
      .flatMap((tdl) => tdl.items)
      .filter((item) => item.completionTracking);
    const result = {
      tasks: itemsWithCompletionTracking.length,
      completed: itemsWithCompletionTracking.filter((q) => q.instance.completed)
        .length,
    };
    map.set(item.sop.sopRelationUuid, result);
  });
  return map;
});

onMounted(() => {
  void initTicketSops();
});

const sopOverview = computed(() =>
  (ticketSops.value ?? []).map((sop) => ({
    sop: sop,
    name: sop.name,
    title: sop.title,
    tasks: sop.todoLists[0]?.items?.length ?? "-",
    documents: sop.documents.length,
  })),
);

// Add Sops

const addSopVisible = ref(false);

function showAddSop() {
  addSopVisible.value = true;
}

const selectedSop = ref<SOP | null>(null);
const title = ref("");

watch(selectedSop, (sop) => {
  if (sop && selectedSop.value) {
    title.value = selectedSop.value.name;
  }
});

const addSopAvailable = computed(
  () => selectedSop.value !== null && title.value !== "",
);

async function addSelectedSop() {
  if (addSopAvailable.value && selectedSop.value) {
    await addSopToTicket(
      props.ticket.uuid,
      selectedSop.value.uuid,
      title.value,
    );
    void initTicketSops();
    closeAddSopDialog();
  }
}

function closeAddSopDialog() {
  addSopVisible.value = false;
  selectedSop.value = null;
}

// Complete TodoListItem

const ticketStatus = computed(
  () =>
    props.ticket.properties!.find((p) => p.customId === ticketProps.status)
      ?.propertyValue?.value as string | undefined,
);

const isTicketInProcess = computed(
  () => ticketStatus.value === statuses.inProcess,
);

const isBeforeCompleted = computed(() => {
  return [
    statuses.draft,
    statuses.planned,
    statuses.new,
    statuses.inProcess,
  ].includes(ticketStatus.value);
});

const isTicketFinished = computed(() =>
  [statuses.completed, statuses.confirmed, statuses.rejected].includes(
    ticketStatus.value,
  ),
);

function setTodoItemChecked(
  checked: boolean,
  sopId: string,
  item: TodoListItem,
) {
  if (!isTicketInProcess.value) {
    // Don't edit if ticket is not in process
    return;
  }
  void updateTodoInstanceItem(sopId, item.uuid, { completed: checked });
}

const { t } = useI18n();

async function updateTodoInstanceItem(
  sopId: string,
  todoListItemId: string,
  patch: { completed?: boolean; feedback?: string },
) {
  const response = await patchTodoItemInstance({
    twinSop: sopId,
    todoListItemUuid: todoListItemId,
    ...patch,
  }).catch((e) => {
    showResponseErrorToUser(e, t);
    throw e;
  });
  if (ticketSopsLoadingState.value.result === undefined) {
    throw new Error(
      "TicketSopsLoadingState is not initialized, cannot update todo item instance",
    );
  }
  const sopIndex = ticketSopsLoadingState.value.result.findIndex(
    (sop) => sop.sopRelationUuid === sopId,
  );
  const todoListIndex = ticketSopsLoadingState.value.result[
    sopIndex
  ].todoLists.findIndex((tdl) =>
    tdl.items.some((t) => t.uuid === todoListItemId),
  );
  const itemIndex = ticketSopsLoadingState.value.result[sopIndex].todoLists[
    todoListIndex
  ].items.findIndex((t) => t.uuid === todoListItemId);

  // NOTE: the response is a) not as specified and b) not an instance object, even if as specified, thus we create the instance object ourself here for now
  const instance = {
    todoInstanceUuid: response.data.uuid,
    completed: response.data.completed,
    feedback: response.data.feedback,
    status: response.data.status,
    createdBy: response.data.createdBy,
    createdAt: response.data.createdAt,
  };

  ticketSopsLoadingState.value.result[sopIndex].todoLists[todoListIndex].items[
    itemIndex
  ].instance = instance;
}

async function saveFeedback(
  feedback: string,
  sopId: string,
  item: TodoListItem,
) {
  await updateTodoInstanceItem(sopId, item.uuid, { feedback });
}

const isAdmin = computed(() => {
  return auth.isAdmin();
});

async function removeSop(id: string) {
  if (ticketSopsLoadingState.value.result === undefined) {
    throw new Error(
      "TicketSopsLoadingState is not initialized, cannot update todo item instance",
    );
  }
  await removeSopFromTicket(id);
  const sopIndex = ticketSopsLoadingState.value.result.findIndex(
    (sop) => sop.sopRelationUuid === id,
  );
  ticketSopsLoadingState.value.result.splice(sopIndex, 1);
}

// Visibility
const hiddenSops = ref<string[]>([]);

function switchVisibility(id: string) {
  if (hiddenSops.value.includes(id)) {
    hiddenSops.value.splice(hiddenSops.value.indexOf(id));
  } else {
    hiddenSops.value.push(id);
  }
}
</script>

<style scoped>
.sop-content {
  background-color: white;
  border-radius: 1rem;
  padding: 1rem;
  overflow: hidden;
}

.sop-checklist {
  align-items: start;
  justify-content: start;
  display: grid;
  grid-template-columns: 15px 2fr 1fr;
  grid-gap: 1rem;
}

.sop-checklist > .el-checkbox.el-checkbox--large {
  height: 18px;
  margin-top: 3px;
}
</style>
